import {
  DataGridPro,
  useGridApiRef,
  type GridRowOrderChangeParams,
} from "@mui/x-data-grid-pro";
import { Typography, Button, Box, IconButton, useMediaQuery } from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import AddAndEditDish from "../Fragments/AddAndEditDish";
import GenericModal from "../../../../components/Modal/GenericModal";
import {
  getDishes,
  updateDishSequence,
} from "../../../../service/BuyerConfiguration/DishManagement/dishService";
import { GridColDef } from "@mui/x-data-grid-pro";
import type { GridValidRowModel } from "@mui/x-data-grid";
import { CustomSwitch } from "../../../../components/Form/CustomSwitch";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AppTheme from "../../../../theme/AppTheme";
import { CssBaseline } from "@mui/material";
import DeleteDish from "../Fragments/DeleteDish";
import {
  notifyError,
  notifySuccess,
} from "../../../../components/ToastContainer/Toast/Toast";
import { reusableDishUpdation } from "../UpdateHelper/reusableDishUpdation";

import DishSequenceTrigger from "../Fragments/DishSequenceTrigger";
import { useTheme } from "@mui/material/styles";

const DishManagementGrid = () => {
  const [openModal, setOpenModal] = useState(false);
  const [dialogMode, setDialogMode] = useState<string | null>(null);
  const [rows, setRows] = useState<any[]>([]);
  const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<number | null>(null);
  const [selectedRowsData, setSelectedRowsData] = useState<any[]>([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const apiRef = useGridApiRef();

  const fetchDishes = useCallback(async () => {
    try {
      const response = await getDishes();
      if (response.statusCode === 200 && response.data.data) {
        setRows(response.data.data || []);
      }
    } catch (error) {
      notifyError(error?.response?.data?.message || "Something went wrong!");
    }
  }, []);

  const handleRowOrderChange = useCallback(
    async (params: GridRowOrderChangeParams) => {
      const { row } = params;
      const orderedIds = apiRef.current.getSortedRowIds();
      const nextRows = orderedIds
        .map((id) => apiRef.current.getRow(id))
        .filter((r): r is GridValidRowModel => r != null);

      const foodType = row.food_type;
      const sameTypeOrdered = nextRows.filter((r) => r.food_type === foodType);
      const new_position =
        sameTypeOrdered.findIndex((r) => r.id === row.id) + 1;

      if (new_position < 1) {
        return;
      }

      try {
        const response = await updateDishSequence(Number(row.id), {
          new_position,
        });
        if (response.statusCode === 200 || response.statusCode === 201) {
          notifySuccess(response.message || "Dish sequence updated successfully");
        }
      } catch (error) {
        notifyError(
          error?.response?.data?.message ||
            "Something went wrong! while updating dish sequence"
        );
      } finally {
        fetchDishes();
      }
    },
    [fetchDishes]
  );

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      width: 100,
      flex: 1,
      align: "center",
    },
    {
      field: "sequence",
      headerName: "Sequence",
      width: 20,
      flex: 1,
      align: "center",
    },
    {
      field: "icon",
      headerName: "Icon",
      width: 50,
      flex: 1,
      renderCell: (params) => {
        return (
          <img
            src={params.value}
            alt="Icon"
            width={50}
            height={50}
            style={{ objectFit: "contain" }}
          />
        );
      },
    },
    { field: "name", headerName: "Name", width: 100, flex: 1 },
    { field: "food_type", headerName: "Food Type", width: 100, flex: 1 },
    {
      field: "is_active_now",
      headerName: "Active Now",
      width: 100,
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color={params.value ? "success.main" : "text.secondary"}>
            {params.value ? "Yes" : "No"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      flex: 1,
      renderCell: (params) => {
        return (
          <CustomSwitch
            checked={Boolean(params.row.status)}
            onChange={(event) =>
              reusableDishUpdation({
                id: params.row.id,
                payload: { status: event },
                fetchData: fetchDishes,
                closeModal: () => setOpenModal(false),
              })
            }
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return (
          <Box display="flex" gap={1}>
            <IconButton
              color="primary"
              onClick={() => {
                setOpenModal(true);
                setDialogMode("EditDish");
                setSelectedRowId(params.row.id);
              }}
            >
              <EditIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
            <IconButton
              color="primary"
              onClick={() => {
                setSelectedDeleteId(params.row.id);
                handleDelete(params.row.id);
                setSelectedRowsData(params.row);
              }}
            >
              <DeleteIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  const handleDelete = (id: number) => {
    setSelectedDeleteId(id);
    setOpenDeleteDialog(true);
  };

  useEffect(() => {
    fetchDishes();
  }, [fetchDishes]);

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <Box sx={{ width: "100%", pr: { xs: 0, md: 2 } }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            rowGap: 1.5,
          }}
        >
          <Typography component="h2" variant="h6">
            Dish Management List
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: isMobile ? "nowrap" : "wrap",
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              gap: isMobile ? 0.75 : 1,
              overflow: "hidden",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              sx={{
                minWidth: 0,
                width: "auto",
                flex: isMobile ? "0 0 85%" : "0 0 auto",
              }}
              onClick={() => {
                setSelectedRowId(null);
                setOpenModal(true);
                setDialogMode("AddDish");
              }}
            >
              Add Dish
            </Button>

            {isMobile ? (
              <Box
                sx={{
                  minWidth: 0,
                  flex: "0 0 15%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <DishSequenceTrigger rows={rows} fetchDishes={fetchDishes} />
              </Box>
            ) : null}
          </Box>
        </Box>
        <Box sx={{ height: { xs: 500, sm: 550 }, width: "100%", overflowX: "auto" }}>
          <Box sx={{ minWidth: 860, height: "100%" }}>
            <DataGridPro
              apiRef={apiRef}
              rows={rows}
              columns={columns as any}
              getRowId={(row) => row.id}
              rowReordering
              disableColumnMenu
              onRowOrderChange={handleRowOrderChange}
              onRowSelectionModelChange={(ids) => {
                setSelectedRowId(ids.length ? (ids[0] as number) : null);
              }}
              sx={{
                "& .MuiDataGrid-rowReorderCell": {
                  cursor: "grab",
                },
                "& .MuiDataGrid-rowReorderCell--draggable": {
                  cursor: "grab",
                },
                "& .MuiDataGrid-dragIcon": {
                  display: "none !important",
                },
                "& .MuiDataGrid-row--dragging": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "#444" : "#e3f2fd",
                  color: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.primary.main
                      : theme.palette.primary.dark,
                },
                "& .MuiDataGrid-row--dragOver": {
                  borderBottom: "2px solid #1976d2",
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Add Edit Modal */}
      <GenericModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={dialogMode === "AddDish" ? "Add Dish" : "Update Dish"}
      >
        <AddAndEditDish
          fetchDishes={fetchDishes}
          dialogMode={dialogMode}
          selectedRowId={selectedRowId}
          setOpenModal={setOpenModal}
          setDialogMode={setDialogMode}
        />
      </GenericModal>

      {/* Delete Modal */}
      <GenericModal
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        title="Confirm Delete"
      >
        {selectedDeleteId && (
          <DeleteDish
            selectedDeleteId={selectedDeleteId}
            setOpenDeleteDialog={setOpenDeleteDialog}
            fetchDishes={fetchDishes}
            setSelectedDeleteId={setSelectedDeleteId}
            selectedRowsData={selectedRowsData}
          />
        )}
      </GenericModal>
    </AppTheme>
  );
};

export default DishManagementGrid;
